import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import StateMachineContext from '@/contexts/StateMachineContext';
import ReactFlow, {
  addEdge,
  applyNodeChanges,
  Connection,
  Edge,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  Node,
  Background,
  BackgroundVariant,
  EdgeChange,
  MarkerType,
} from 'reactflow';
import { useLocation, useParams } from 'react-router-dom';
import { StateMachineSidebar } from './Sidebar';
import StateMachineService from '../../domain/StateMachine/StateMachineService';
import NodeComponent from './NodeComponent';
import ActionModal from './NodeComponentModals/ActionModal';
import TransitionModal from './NodeComponentModals/TransitionModal';
import { notification } from 'antd';

// const nodeTypes = { parameter: ParameterNode };
const nodeTypes = { node: NodeComponent };

const getColor = (type: any) => {
  switch (type) {
    case 'default': {
      return 'green';
    }
    case 'input': {
      return 'blue';
    }
    case 'output': {
      return 'red';
    }
    default: {
      return 'gray';
    }
  }
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

let id = 0;
const getId = () => `${id++}`;

const stateMachineService = new StateMachineService();

export default function EditStateMachineView() {
  const query = useQuery();
  const isEdit = query.get('isEdit');
  const { stateMachineId } = useParams();

  const [contextValue, setContextValue] = useState<any>({
    inputsList: [],
    outputsList: [],
    constantsList: [],
    strategyList: [],
  });
  const reactFlowWrapper = useRef<any>(null);

  const [nodes, setNodes] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [nodeSelect, setNodeSelect] = useState<any>(null);
  const [stateMachineDetails, setStateMachineDetails] = useState<any>(null);
  const [showActionModal, setShowActionModal] = useState<boolean>(false);
  const [showTransitionModal, setShowTransitionModal] =
    useState<boolean>(false);
  const [transition, setTransition] = useState<any>({
    source: null,
    target: null,
  });
  const [transitionsList, setTransitionsList] = useState<any[]>([]);
  const [selectedTransition, setSelectedTransition] = useState<any>(null);

  const onNodesChange = useCallback(
    (changes: any) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes],
  );

  const onConnect = useCallback((params: Edge | Connection) => {
    setNodes((prev) => {
      const s = prev.find((n: any) => n.id == params.source);
      const t = prev.find((n: any) => n.id == params.target);
      setTransition({ source: s, target: t });
      return prev;
    });
    setShowTransitionModal(true);
    setSelectedTransition(null);
    setEdges((eds) => {
      const newEdges = addEdge(
        {
          ...params,
          type: 'straight',
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
        },
        eds,
      );

      setNodes((nds) => {
        const group = getConnectedGroup(params.source!, nds, newEdges);
        const initialNodes = nds.filter((n) => group.has(n.id) && n.data.initial);
        if (initialNodes.length <= 1) return nds;

        const keeper = initialNodes.reduce((best, n) =>
          (n.data.priority || 99) < (best.data.priority || 99) ? n : best
        );

        return nds.map((node) => {
          if (!group.has(node.id)) return node;
          if (node.data.initial && node.id !== keeper.id) {
            return {
              ...node,
              data: {
                ...node.data,
                initial: false,
                priority: 0,
              },
            };
          }
          return node;
        });
      });

      return newEdges;
    });
  }, []);

  const openActionModal = (data: any, id: any) => {
    let ns: any = null;
    setNodes((prev) => {
      ns = prev.find((n: any) => n.id == id);
      return prev;
    });
    setNodeSelect(ns);
    setShowActionModal(true);
  };

  function onChangeNode(newData: any, id: any) {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id != id) {
          return node;
        }

        // const label = data.paremeterName;
        // const value = data;

        return {
          ...node,
          data: {
            ...node.data,
            value: newData,
          },
        };
      }),
    );
  }

  function onChangeLabel(label: any, id: any) {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id != id) {
          return node;
        }

        return {
          ...node,
          data: {
            ...node.data,
            label: label,
          },
        };
      }),
    );
  }

  function getConnectedGroup(nodeId: string, allNodes: Node[], allEdges: Edge[]): Set<string> {
    const group = new Set<string>();
    const queue = [nodeId];
    while (queue.length > 0) {
      const current = queue.pop()!;
      if (group.has(current)) continue;
      group.add(current);
      for (const edge of allEdges) {
        if (edge.source === current && !group.has(edge.target)) {
          queue.push(edge.target);
        }
        if (edge.target === current && !group.has(edge.source)) {
          queue.push(edge.source);
        }
      }
    }
    return group;
  }

  function getNextPriority(nds: Node[], currentGroupIds: Set<string>): number {
    const usedPriorities = nds
      .filter((n) => !currentGroupIds.has(n.id) && n.data.initial && n.data.priority)
      .map((n) => n.data.priority as number);
    for (let p = 1; p <= 5; p++) {
      if (!usedPriorities.includes(p)) return p;
    }
    return 1;
  }

  function setInitial(id: any) {
    setNodes((nds) => {
      const group = getConnectedGroup(id, nds, edges);
      const nextPriority = getNextPriority(nds, group);
      return nds.map((node) => {
        if (!group.has(node.id)) return node;
        if (node.id != id) {
          return {
            ...node,
            data: {
              ...node.data,
              initial: false,
            },
          };
        } else {
          return {
            ...node,
            data: {
              ...node.data,
              initial: true,
              enabled: true,
              priority: nextPriority,
            },
          };
        }
      });
    });
  }

  function removeInitialState(id: any) {
    setNodes((nds) => {
      return nds.map((node) => {
        if (node.id != id) return node;
        return {
          ...node,
          data: {
            ...node.data,
            initial: false,
            priority: 0,
          },
        };
      });
    });
  }

  function setDisabled(id: any) {
    setNodes((nds) => {
      return nds.map((node) => {
        if (node.id != id) return node;
        return {
          ...node,
          data: {
            ...node.data,
            enabled: false,
            initial: false,
            priority: 0,
          },
        };
      });
    });
  }

  function setEnabled(id: any) {
    setNodes((nds) => {
      return nds.map((node) => {
        if (node.id != id) return node;
        return {
          ...node,
          data: {
            ...node.data,
            enabled: true,
          },
        };
      });
    });
  }

  function updatePriority(id: any, priority: number) {
    setNodes((nds) => {
      return nds.map((node) => {
        if (node.id != id) return node;
        const newPriority = Math.max(1, Math.min(5, priority));
        return {
          ...node,
          data: {
            ...node.data,
            priority: newPriority,
          },
        };
      });
    });
  }

  const onDrop = useCallback(
    (event: {
      preventDefault: () => void;
      dataTransfer: { getData: (arg0: string) => any };
      clientX: number;
      clientY: number;
    }) => {
      event.preventDefault();

      const reactFlowBounds =
        reactFlowWrapper?.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance?.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
      let color = '';
      color = getColor(type);
      let newNode: Node;
      const newID = getId();
      switch (type) {
        case 'node':
          newNode = {
            id: newID,
            type,
            position,
            data: {
              label: ``,
              value: {},
              initial: false,
              enabled: true,
              priority: 0,
              editable: isEdit,
              onChange: onChangeNode,
              onChangeLabel: onChangeLabel,
              setInitial: setInitial,
              removeInitialState: removeInitialState,
              setDisabled: setDisabled,
              setEnabled: setEnabled,
              updatePriority: updatePriority,
              removeNode: removeNode,
              addNode: addNode,
              openActionModal: openActionModal,
            },
            style: {
              background: color,
            },
          } as Node;
          break;

        default:
          newNode = {
            id: newID,
            type,
            position,
            data: { label: `${type} node`, value: {}, onChange: onChange },
            style: {
              background: color,
            },
          } as Node;
          break;
      }
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance],
  );

  function onChange(event: any, id: any) {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id != id) {
          return node;
        }

        const label = event.target.value;

        return {
          ...node,
          data: {
            ...node.data,
            label,
          },
        };
      }),
    );
  }

  const removeNode = (id: string) => {
    setNodes((prev) => {
      return prev.filter((e) => {
        return e.id != id;
      });
    });
    setEdges((prev) => {
      return prev.filter((e) => {
        return e.source != id && e.target != id;
      });
    });
    setTransitionsList((prev) => {
      return prev.filter((t: any) => {
        return t.fromId != id && t.toId != id;
      });
    });
  };

  const addNode = (id: string) => {
    onAddNode(id);
  };

  function onAddNode(id: string) {
    const newID = getId();
    setNodes((prev) => {
      const curentNode = prev.find((node) => {
        return node.id == id;
      });

      const newNode = {
        id: newID,
        type: 'parameter',
        position: {
          x: (curentNode?.position.x || 0) + 250,
          y: curentNode?.position.y,
        },
        data: {
          label: `Parameter node`,
          value: {},
          onChange: onChangeNode,
          removeNode: removeNode,
          addNode: addNode,
        },
        style: {
          background: getColor('parameter'),
        },
      } as unknown as Node;
      return [...prev, newNode];
    });
    const newEdge = {
      id: `reactflow__edge-${id}-${newID}`,
      source: id,
      target: newID,
      type: 'smoothstep',
    };
    setEdges((prev) => {
      return [...prev, newEdge];
    });
  }

  const onDragOver = useCallback(
    (event: {
      preventDefault: () => void;
      dataTransfer: { dropEffect: string };
    }) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },
    [],
  );

  const handleGetDetails = async () => {
    if (stateMachineId) {
      const response =
        await stateMachineService.getStateMachineDetail(stateMachineId);
      setStateMachineDetails(response);
      const nodes = JSON.parse(response.nodes);
      if (nodes.length > 0) {
        setNodes(
          JSON.parse(response.nodes).map((node: any) => {
            return {
              ...node,
              data: {
                ...node.data,
                enabled: node.data.enabled !== undefined ? node.data.enabled : true,
                priority: node.data.priority || 0,
                onChange: onChangeNode,
                onChangeLabel: onChangeLabel,
                setInitial: setInitial,
                removeInitialState: removeInitialState,
                setDisabled: setDisabled,
                setEnabled: setEnabled,
                updatePriority: updatePriority,
                removeNode: removeNode,
                addNode: addNode,
                openActionModal: openActionModal,
              },
            };
          }),
        );
        id = nodes.length > 0 ? parseInt(nodes[nodes.length - 1].id) + 1 : 1;
      }
      if (response.edges.length > 0) {
        setEdges(JSON.parse(response.edges));
      }
      if (response.definition.length > 0) {
        const definition = JSON.parse(response.definition);
        const variables = definition.variables;
        const newContext = {
          inputsList: variables.inputs.map((inp: string) => {
            const values = inp.split('.');
            return {
              label: inp,
              assetType: values[1],
              assetId: values[2],
              attribute: values[3],
            };
          }),
          outputsList: (variables.outputs || []).map((out: string) => {
            const values = out.split('.');
            return {
              label: out,
              assetType: values[1],
              assetId: values[2],
              attribute: values[3],
            };
          }),
          constantsList: variables.constants.map((constant: any) => {
            return {
              ...constant,
              label: `Constants.${constant.name}=${constant.value}`,
            };
          }),
          strategyList: variables.strategies.map((strategy: any) => {
            return {
              ...strategy,
              label: strategy.name,
            };
          }),
        };

        setContextValue(newContext);
        setTransitionsList(definition.transitions);
      }
    }
  };

  const handleSaveStateMachine = async () => {
    const body = {
      id: stateMachineDetails.id,
      name: stateMachineDetails.name,
      enabled: stateMachineDetails.enabled,
      inpId: stateMachineDetails.inpId,
      locationId: stateMachineDetails.locationId,
      nodes: JSON.stringify(nodes),
      edges: JSON.stringify(edges),
      definition: JSON.stringify({
        state: nodes.map((node: any) => {
          const action = { ...node.data.value };

          delete action.statements;

          if (!action.statement) {
            action.statement = 'if 1 == 1:\n  h = 1';
          }

          if (action.input) {
            action.inputs = action.input;
            delete action.input;
          }
          if (action.outputs) {
            action.output = action.outputs;
            delete action.outputs;
          }
          const stateObject: any = {
            initial: node.data.initial,
            enabled: node.data.enabled !== undefined ? node.data.enabled : true,
            tag: node.data.label,
            action,
          };
          if (node.data.initial) {
            stateObject.priority = node.data.priority || 1;
          }
          return stateObject;
        }),
        variables: {
          inputs: contextValue.inputsList.map((inp: any) => inp.label),
          outputs: contextValue.outputsList.map((out: any) => out.label),
          constants: contextValue.constantsList.map((constant: any) => ({
            name: constant.name,
            value: constant.value,
            strategies: constant.strategies || [],
          })),
          strategies: contextValue.strategyList.map((strategy: any) => ({
            name: strategy.name,
          })),
        },
        transitions: transitionsList.map((transition) => {
          const from = nodes.find(
            (node) => node.id == transition.fromId,
          ) as any;
          const to = nodes.find((node) => node.id == transition.toId) as any;
          return {
            fromId: transition.fromId,
            toId: transition.toId,
            from: from.data?.label,
            to: to.data?.label,
            condition: transition.condition,
          };
        }),
      }),
    };
    try {
      const response = await stateMachineService.editStateMachineComplete(body);
      if (response) {
        notification.success({
          message: 'Successful',
          description: 'Smart control edited successfully',
        });
      }
    } catch (error) {
      console.error('Error updating smart control:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update smart control',
      });
    }
  };

  useEffect(() => {
    handleGetDetails();
  }, []);

  return (
    <StateMachineContext.Provider
      value={{
        value: contextValue,
        setValue: setContextValue,
      }}
    >
      <div className="containerDiagramBlock">
        <div className="dndflow">
          <ReactFlowProvider>
            <div className="reactflow-wrapper" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={(edgeChanges) => {
                  onEdgesChange(edgeChanges);
                }}
                onEdgeClick={(event, edges) => {
                  setNodes((prev) => {
                    const s = prev.find((n: any) => n.id == edges.source);
                    const t = prev.find((n: any) => n.id == edges.target);
                    setTransition({ source: s, target: t });
                    return prev;
                  });
                  const find = transitionsList.find(
                    (t) => t.fromId == edges.source && t.toId == edges.target,
                  );
                  setSelectedTransition(find);
                  setShowTransitionModal(true);
                }}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                nodeTypes={nodeTypes}
                fitView
              >
                {/* <Controls /> */}
                <Background
                  color="#81818a"
                  gap={16}
                  variant={BackgroundVariant.Dots}
                />
              </ReactFlow>
            </div>
            <StateMachineSidebar
              onSave={() => {
                handleSaveStateMachine();
              }}
              isEditMode={isEditMode}
              nodeSelect={nodeSelect}
              onChangeNode={onChangeNode}
              closeEditMode={() => {
                setIsEditMode(false);
              }}
              stateMachineDetails={stateMachineDetails}
            />
          </ReactFlowProvider>
        </div>
      </div>
      {showActionModal && (
        <ActionModal
          selectedNode={nodeSelect}
          onCancel={() => {
            setShowActionModal(false);
          }}
        />
      )}
      {showTransitionModal && (
        <TransitionModal
          selectedTransition={selectedTransition}
          source={transition.source}
          target={transition.target}
          onCancel={() => {
            setShowTransitionModal(false);
          }}
          saveChanges={(data: any) => {
            setTransitionsList((prev: any) => {
              let copy = [...prev];
              const finded = prev.findIndex((t: any) => {
                return t.fromId == data.fromId && t.toId == data.toId;
              });
              if (finded >= 0) {
                copy[finded].condition = data.condition;
              } else {
                copy.push(data);
              }
              return copy;
            });
          }}
          deleteTransition={(data: any) => {
            setTransitionsList((prev: any) => {
              return prev.filter((t: any) => {
                return !(t.fromId == data.fromId && t.toId == data.toId);
              });
            });
            setEdges((prev) => {
              return prev.filter((e) => {
                return !(e.source == data.fromId && e.target == data.toId);
              });
            });
          }}
        />
      )}
    </StateMachineContext.Provider>
  );
}
